import type { RootState } from "../../store";
import type { IQueueEvent } from "../../types";
import { FilterKey, filters } from "./filters";

export type SocketState = Pick<RootState, "socket">;

/**
 * Selects if the metrics are available. Metrics are available when the current event has metrics defined. aka. There is a valid event.
 * @param state
 * @returns True if the metrics are available, false otherwise.
 */
export const selectMetricsAreAvailable = (state: SocketState): boolean => {
  const recentEvent = selectRecentEvent(state);
  const areMetricsAvailable = Object.keys(recentEvent).length > 0;
  return areMetricsAvailable;
};

const getFilteredQueue = (state: SocketState): IQueueEvent[] => {
  const filterFn = filters[state.socket.currentFilterKey as FilterKey];
  const filteredQueue = state.socket.eventQueue.filter(filterFn);
  return filteredQueue;
};

/**
 * Selects the most recent event in the queue. This is basically list[0]. It is important to highlight that the event is filtered by the current filter set in the store.
 * @param state
 */
export const selectRecentEvent = (state: SocketState): IQueueEvent => {
  const filteredQueue = getFilteredQueue(state);
  if (filteredQueue.length === 0) return {} as IQueueEvent;
  const recentEvent = filteredQueue[0];
  if (recentEvent.tagData) return {} as IQueueEvent;
  return recentEvent;
};

/**
 * Selects the most recent event in the queue without filtering by tag status.
 * Used for review workflows (Coach Mode, Bullpen Mode) where events should persist
 * for visualization and analysis regardless of whether they've been tagged.
 * @param state
 */
export const selectRecentEventForReview = (state: SocketState): IQueueEvent => {
  const filteredQueue = getFilteredQueue(state);
  if (filteredQueue.length === 0) return {} as IQueueEvent;
  const recentEvent = filteredQueue[0];
  return recentEvent;
};

/**
 * Selects the event currently being tagged
 * @param state
 */
export const selectFocusedEvent = (state: SocketState): IQueueEvent => {
  const hasPayload = Object.keys(state.socket.focusedEventPayload).length > 0;
  if (!hasPayload) return {} as IQueueEvent;

  const focusedEventPayload = state.socket.focusedEventPayload;
  const eventQueue = selectEventQueue(state);
  const focusedEvent = eventQueue.find((event) =>
    event.contributing_events.includes(focusedEventPayload.event_uuid),
  );
  if (!focusedEvent) return {} as IQueueEvent;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { event_uuid, ...rest } = focusedEventPayload;
  return { ...focusedEvent, ...rest } as IQueueEvent;
};

/**
 * Selects all the events received from the socket. It is important to highlight that the events are filtered by the current filter set in the store.
 * @param state
 */
export const selectEventQueue = (state: SocketState): IQueueEvent[] => {
  const filterFn = filters[state.socket.currentFilterKey as FilterKey];
  const filteredQueue = state.socket.eventQueue.filter(filterFn);
  return filteredQueue;
};

/**
 * Selects if the game (based on the latest event) is a softball game
 * @param state
 */
export const selectIsSoftball = (state: RootState): boolean => {
  return state.session.sport?.toLowerCase() === "softball";
};
